import React, { useEffect, useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import PaginationList from '../../../Shared/Components/PaginationList'
import Row from 'react-bootstrap/Row'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'

const PROCESSING_POLL_MS = 15000

const isProcessing = (row) => row.type === 'mp4' && row.video_status === 'processing'

function TypeBadge({ row }) {
    if (row.type === 'youtube') {
        return (
            <span className={`badge ${row.youtube_url ? 'badge-light-danger' : 'badge-light'}`}>
                {row.youtube_url ? 'YouTube' : 'YouTube (sin URL)'}
            </span>
        )
    }

    if (row.type === 'mp4') {
        if (row.video_status === 'processing') {
            return (
                <span className="badge badge-light-warning d-inline-flex align-items-center gap-2">
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Procesando…
                </span>
            )
        }
        if (row.video_status === 'failed') {
            return (
                <span className="badge badge-light-danger" title={row.video_error || ''}>
                    MP4 · Error al procesar
                </span>
            )
        }
        if (row.video_status === 'ready') {
            return <span className="badge badge-light-success">MP4</span>
        }
        return <span className="badge badge-light">MP4 (sin archivo)</span>
    }

    return (
        <span className={`badge ${row.file_path ? 'badge-light-primary' : 'badge-light'}`}>
            {row.file_path ? 'PDF' : 'PDF (sin archivo)'}
        </span>
    )
}

const CulturaInstitucionalIndex = ({ materials, filters }) => {
    const { flash } = usePage().props
    const data = materials.data
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const anyProcessing = data.some(isProcessing)

    useEffect(() => {
        if (!anyProcessing) return undefined
        const timer = setInterval(() => {
            Inertia.reload({ only: ['materials'], preserveScroll: true, preserveState: true })
        }, PROCESSING_POLL_MS)
        return () => clearInterval(timer)
    }, [anyProcessing])

    const filterForm = useForm({
        search: filters.search || '',
    })

    const submitFilters = (e) => {
        e.preventDefault()
        filterForm.get(route('sia.manage.cultura-institucional.index'), { preserveState: true })
    }

    const clearFilters = () => {
        filterForm.setData({ search: '' })
        filterForm.get(route('sia.manage.cultura-institucional.index'))
    }

    const paginationFilters = {
        search: filters.search || undefined,
    }

    const listPath = 'admin/sia/manage/cultura-institucional'

    const confirmDelete = () => {
        if (!deleteTarget) return
        setDeleting(true)
        Inertia.delete(route('sia.manage.cultura-institucional.destroy', deleteTarget.id), {
            preserveScroll: true,
            onFinish: () => {
                setDeleting(false)
                setDeleteTarget(null)
            },
        })
    }

    return (
        <Row>
            {flash.success && <AlertDismissible message={flash.success} type="success" />}
            {flash.error && <AlertDismissible message={flash.error} type="danger" />}
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath={listPath} />
            </span>

            <div className="card card-flush h-md-100">
                <div className="card-header pt-7">
                    <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-gray-800">Cultura institucional — Material (SIA)</span>
                    </h3>
                    <div className="card-toolbar">
                        <Link href={route('sia.manage.cultura-institucional.create')} className="btn btn-sm btn-primary">
                            Agregar material
                        </Link>
                    </div>
                </div>

                <div className="card-body pt-6">
                    <Form onSubmit={submitFilters} className="d-flex flex-wrap align-items-end gap-3 mb-8">
                        <div className="flex-grow-1" style={{ minWidth: '220px', maxWidth: '420px' }}>
                            <label className="form-label fs-7 fw-bold text-gray-600 mb-2">Buscar</label>
                            <Form.Control
                                type="search"
                                className="form-control form-control-sm form-control-solid"
                                placeholder="Título…"
                                value={filterForm.data.search}
                                onChange={(e) => filterForm.setData('search', e.target.value)}
                            />
                        </div>
                        <Button type="submit" size="sm" variant="primary" disabled={filterForm.processing}>
                            Buscar
                        </Button>
                        <Button type="button" size="sm" variant="light" onClick={clearFilters}>
                            Limpiar
                        </Button>
                    </Form>

                    <div className="table-responsive">
                        <table className="table table-row-dashed align-middle gs-0 gy-3 my-0">
                            <thead>
                                <tr className="fs-7 fw-bold text-gray-400 border-bottom-0">
                                    <th className="p-0 pb-3 min-w-50px text-start">ID</th>
                                    <th className="p-0 pb-3 min-w-250px text-start">Título</th>
                                    <th className="p-0 pb-3 min-w-75px text-start">Orden</th>
                                    <th className="p-0 pb-3 min-w-100px text-start">Tipo</th>
                                    <th className="p-0 pb-3 min-w-125px text-end">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 && (
                                    <tr>
                                        <td className="text-center py-10 text-gray-700" colSpan={5}>
                                            No hay materiales registrados.
                                        </td>
                                    </tr>
                                )}
                                {data.map((row) => (
                                    <tr key={row.id}>
                                        <td className="text-gray-800">{row.id}</td>
                                        <td className="text-gray-900 fw-semibold">{row.title}</td>
                                        <td>{row.sort_order}</td>
                                        <td>
                                            <TypeBadge row={row} />
                                        </td>
                                        <td className="text-end">
                                            {isProcessing(row) ? (
                                                <>
                                                    <span className="text-muted fs-8 me-3">Disponible al terminar</span>
                                                    <button type="button" className="btn btn-sm btn-light-primary me-2" disabled>
                                                        Editar
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-light-danger" disabled>
                                                        Eliminar
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <Link
                                                        href={route('sia.manage.cultura-institucional.edit', row.id)}
                                                        className="btn btn-sm btn-light-primary me-2"
                                                    >
                                                        Editar
                                                    </Link>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-light-danger"
                                                        onClick={() => setDeleteTarget(row)}
                                                    >
                                                        Eliminar
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-center py-6">
                        <PaginationList links={materials.links} filters={paginationFilters} />
                    </div>
                </div>
            </div>

            <Modal show={deleteTarget !== null} onHide={() => !deleting && setDeleteTarget(null)} centered>
                <Modal.Header closeButton={!deleting}>
                    <Modal.Title>Confirmar eliminación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Eliminar «{deleteTarget?.title}»?
                    {deleteTarget?.file_path || deleteTarget?.video_path ? ' Se borrará también el archivo en servidor.' : ''}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" disabled={deleting} onClick={() => setDeleteTarget(null)}>
                        Cancelar
                    </Button>
                    <Button variant="danger" disabled={deleting} onClick={confirmDelete}>
                        {deleting ? 'Eliminando…' : 'Eliminar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Row>
    )
}

CulturaInstitucionalIndex.layout = (page) => <Layout title="Cultura institucional SIA">{page}</Layout>

export default CulturaInstitucionalIndex
